import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '../components/router/authContext';

const TutorialContext = createContext(null);

const STORAGE_PREFIX = 'efleetplan.tutorial';
const CURRENT_VERSION = 1;

const getStorageKey = (userId) =>
  `${STORAGE_PREFIX}.v${CURRENT_VERSION}.${userId || 'anonymous'}.completed`;

// Delay between a tour ending and the next queued tour starting (ms)
const QUEUE_RESUME_DELAY_MS = 500;

export const TutorialProvider = ({ children }) => {
  const { currentUser, isLoading: authLoading } = useAuth();

  // Active tour: { id: string, steps: TutorialStep[] } | null
  const [activeTour, setActiveTour] = useState(null);
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  // Queue: ordered list of { id, steps } waiting to run after the active tour ends.
  // Tours are pushed in FIFO order; the front item starts after the active tour completes.
  const [tourQueue, setTourQueue] = useState([]);
  // Skipping a tour suppresses all automatic tours until the page is reloaded.
  // A user can still explicitly reopen an individual guide.
  const autoToursSuppressedRef = useRef(false);

  // ─── Persistence helpers ──────────────────────────────────────────────────

  const getCompleted = useCallback(() => {
    try {
      const key = getStorageKey(currentUser?.id);
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, [currentUser?.id]);

  const markCompleted = useCallback(
    (tourId) => {
      try {
        const key = getStorageKey(currentUser?.id);
        const completed = getCompleted();
        completed[tourId] = {
          completedAt: new Date().toISOString(),
          version: CURRENT_VERSION,
        };
        localStorage.setItem(key, JSON.stringify(completed));
      } catch (e) {
        console.error('Failed to persist tutorial completion', e);
      }
    },
    [currentUser?.id, getCompleted]
  );

  const isTourCompleted = useCallback(
    (tourId) => {
      if (authLoading) return true; // don't auto-start while auth resolves
      const completed = getCompleted();
      return Boolean(completed[tourId]);
    },
    [authLoading, getCompleted]
  );

  const resetTour = useCallback(
    (tourId) => {
      try {
        const key = getStorageKey(currentUser?.id);
        const completed = getCompleted();
        delete completed[tourId];
        localStorage.setItem(key, JSON.stringify(completed));
      } catch (e) {
        console.error('Failed to reset tutorial', e);
      }
    },
    [currentUser?.id, getCompleted]
  );

  // ─── Tour lifecycle ───────────────────────────────────────────────────────

  /**
   * Start a tour.
   * If another tour is already active, the new tour is pushed to the queue
   * and will start automatically after the active tour ends or is skipped.
   */
  const startTour = useCallback((tourId, steps, { manual = false } = {}) => {
    if (!tourId || !Array.isArray(steps) || steps.length === 0) return;
    if (!manual && autoToursSuppressedRef.current) return;

    setActiveTour((current) => {
      if (current) {
        // A tour is already running — queue this one instead of replacing it.
        setTourQueue((q) => {
          // Avoid duplicates: don't queue the same tour twice.
          if (q.some((t) => t.id === tourId)) return q;
          return [...q, { id: tourId, steps }];
        });
        return current; // keep the existing tour active
      }
      // No active tour — start immediately.
      setActiveStepIndex(0);
      return { id: tourId, steps };
    });
  }, []);

  /** Advance to the next step, or end the tour if on the last step. */
  const nextStep = useCallback(() => {
    setActiveTour((current) => {
      if (!current) return null;
      const nextIndex = activeStepIndex + 1;
      if (nextIndex >= current.steps.length) {
        markCompleted(current.id);
        setActiveStepIndex(0);
        return null; // signals tour ended → queue effect fires
      }
      setActiveStepIndex(nextIndex);
      return current;
    });
  }, [activeStepIndex, markCompleted]);

  const prevStep = useCallback(() => {
    setActiveStepIndex((i) => Math.max(0, i - 1));
  }, []);

  /** Skip the active tour and suppress further automatic tours this session. */
  const skipTour = useCallback(() => {
    autoToursSuppressedRef.current = true;
    setActiveTour((current) => {
      if (!current) return null;
      markCompleted(current.id);
      setActiveStepIndex(0);
      return null;
    });
    setTourQueue([]);
  }, [markCompleted]);

  /**
   * When the active tour ends (activeTour → null) and there are queued tours,
   * pop the next one from the front of the queue and start it after a short pause.
   */
  useEffect(() => {
    if (activeTour !== null || tourQueue.length === 0) return;

    const timer = setTimeout(() => {
        setTourQueue((q) => {
        if (q.length === 0) return q;
        const [next, ...rest] = q;
        setActiveTour(next);
        setActiveStepIndex(0);
        return rest;
      });
    }, QUEUE_RESUME_DELAY_MS);

    return () => clearTimeout(timer);
  }, [activeTour, tourQueue.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Keyboard shortcuts ───────────────────────────────────────────────────

  useEffect(() => {
    if (!activeTour) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') skipTour();
      if (e.key === 'ArrowRight') nextStep();
      if (e.key === 'ArrowLeft') prevStep();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [activeTour, skipTour, nextStep, prevStep]);

  // ─── Dev console helpers ──────────────────────────────────────────────────
  //   window.tutorial.resetTour('welcome_home')
  //   window.tutorial.resetAll()
  //   window.tutorial.listTours()
  //   window.tutorial.listQueue()
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.tutorial = {
      resetTour: (tourId) => {
        resetTour(tourId);
        console.info(`[Tutorial] Reset tour: "${tourId}". Re-enter the triggering page to see it again.`);
      },
      resetAll: () => {
        try {
          const key = getStorageKey(currentUser?.id);
          localStorage.removeItem(key);
          setTourQueue([]);
          console.info('[Tutorial] All tours reset. Reload the page to see them again.');
        } catch (e) {
          console.error(e);
        }
      },
      startTour: (tourId, steps) => {
        startTour(tourId, steps || [], { manual: true });
        console.info(`[Tutorial] Queued or started tour: "${tourId}"`);
      },
      skipTour,
      nextStep,
      prevStep,
      listTours: () => {
        try {
          const key = getStorageKey(currentUser?.id);
          const raw = localStorage.getItem(key);
          const completed = raw ? JSON.parse(raw) : {};
          console.table(completed);
          return completed;
        } catch {
          return {};
        }
      },
      listQueue: () => {
        console.log('[Tutorial] Active:', activeTour?.id ?? 'none');
        console.log('[Tutorial] Queue:', tourQueue.map((t) => t.id));
        return { active: activeTour?.id ?? null, queue: tourQueue.map((t) => t.id) };
      },
    };
    return () => {
      delete window.tutorial;
    };
  }, [currentUser?.id, activeTour, tourQueue, resetTour, startTour, skipTour, nextStep, prevStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Context value ────────────────────────────────────────────────────────

  const currentStep = activeTour?.steps[activeStepIndex] || null;
  const totalSteps = activeTour?.steps.length || 0;
  const isActive = Boolean(activeTour);

  return (
    <TutorialContext.Provider
      value={{
        isActive,
        activeTour,
        currentStep,
        activeStepIndex,
        totalSteps,
        tourQueue,
        startTour,
        nextStep,
        prevStep,
        skipTour,
        isTourCompleted,
        resetTour,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorialContext = () => {
  const ctx = useContext(TutorialContext);
  if (!ctx) throw new Error('useTutorialContext must be used within TutorialProvider');
  return ctx;
};
